'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { useUserSession } from '@libs/common-next';
import { NAVIGATION_URLS } from '@libs/core';

import {
    clearProfileCompleteCelebrationDismissed,
    hasProfileCompleteCelebration,
    hasProfileCompleteCelebrationDismissed,
    hasProfileCompleteFlowOrigin,
    persistProfileCompleteCelebration,
    resetProfileCompleteCelebrationForAuthEntry,
    resetProfileCompleteCelebrationState,
} from './celebration';
import { getProfileCelebrationUserScope } from './celebration-user-scope';
import type { ProfileCompletionReturnTo } from './constants';
import { PROFILE_COMPLETE_FROM_PARAM } from './constants';
import { useDismissProfileCelebrationOutsideMyAccount } from './use-dismiss-profile-celebration-outside-my-account';

const ENTRY_PARAMS_TO_RESET_CELEBRATION = new Set(['create-account', 'sign-in']);

const RETURN_PATH: Record<ProfileCompletionReturnTo, string> = {
    treats: NAVIGATION_URLS.velocity.account.treatOffers,
    'about-you': NAVIGATION_URLS.velocity.account.aboutYou,
};

function wasAlreadyCompleteOnEntry(allStepsComplete: boolean, fromParam: string | null): boolean {
    return (
        allStepsComplete &&
        !hasProfileCompleteCelebration() &&
        fromParam !== PROFILE_COMPLETE_FROM_PARAM &&
        !hasProfileCompleteFlowOrigin()
    );
}

type UseCompleteProfileCelebrationParams = {
    enabled: boolean;
    isProfileComplete: boolean;
    allStepsComplete: boolean;
    completedCount: number;
    totalCount: number;
    isLoading: boolean;
    error: Error | null;
    returnTo: ProfileCompletionReturnTo;
};

export function useCompleteProfileCelebration({
    enabled,
    isProfileComplete,
    allStepsComplete,
    completedCount,
    totalCount,
    isLoading,
    error,
    returnTo,
}: UseCompleteProfileCelebrationParams) {
    const router = useRouter();
    const fromParam = useSearchParams()?.get('from') ?? null;
    const { user } = useUserSession();
    const userScope = getProfileCelebrationUserScope(user);
    const [isCelebrating, setIsCelebrating] = useState(false);
    const [isDismissed, setIsDismissed] = useState(() => hasProfileCompleteCelebrationDismissed(userScope));
    const [isAlreadyCompleteOnLoad, setIsAlreadyCompleteOnLoad] = useState(false);
    const hasRecordedInitialCount = useRef(false);
    const previousCompletedCount = useRef<number | null>(null);
    const alreadyCompleteOnEntryRef = useRef<boolean | null>(null);
    const previousUserScopeRef = useRef<string | null | undefined>(undefined);

    if (alreadyCompleteOnEntryRef.current === null && enabled && !isLoading && !error) {
        alreadyCompleteOnEntryRef.current = wasAlreadyCompleteOnEntry(allStepsComplete, fromParam);
    }

    const shouldHideAsAlreadyComplete =
        allStepsComplete && (isAlreadyCompleteOnLoad || alreadyCompleteOnEntryRef.current === true);

    const stopCelebration = () => {
        resetProfileCompleteCelebrationState();
        setIsCelebrating(false);
    };

    const startCelebration = () => {
        if (hasProfileCompleteCelebrationDismissed(userScope)) {
            return;
        }
        persistProfileCompleteCelebration();
        setIsCelebrating(true);
    };

    useDismissProfileCelebrationOutsideMyAccount({
        enabled,
        onDismiss: () => {
            setIsCelebrating(false);
            setIsDismissed(true);
        },
    });

    useEffect(() => {
        if (fromParam && ENTRY_PARAMS_TO_RESET_CELEBRATION.has(fromParam)) {
            resetProfileCompleteCelebrationForAuthEntry();
            setIsCelebrating(false);
            setIsDismissed(false);
        }
    }, [fromParam]);

    useEffect(() => {
        const previousUserScope = previousUserScopeRef.current;
        previousUserScopeRef.current = userScope;

        if (previousUserScope === undefined) {
            setIsDismissed(hasProfileCompleteCelebrationDismissed(userScope));
            return;
        }

        if (previousUserScope === userScope) {
            return;
        }

        alreadyCompleteOnEntryRef.current = null;
        hasRecordedInitialCount.current = false;
        previousCompletedCount.current = null;
        setIsCelebrating(false);
        setIsAlreadyCompleteOnLoad(false);

        const dismissedForCurrentUser = hasProfileCompleteCelebrationDismissed(userScope);
        if (!dismissedForCurrentUser && hasProfileCompleteCelebrationDismissed()) {
            clearProfileCompleteCelebrationDismissed();
        }
        setIsDismissed(dismissedForCurrentUser);
    }, [userScope]);

    useEffect(() => {
        if (!enabled || isLoading || error || allStepsComplete) {
            return;
        }

        if (
            isDismissed ||
            hasProfileCompleteCelebrationDismissed(userScope) ||
            hasProfileCompleteCelebrationDismissed()
        ) {
            clearProfileCompleteCelebrationDismissed();
            setIsDismissed(false);
        }
    }, [allStepsComplete, enabled, error, isDismissed, isLoading, userScope]);

    useEffect(() => {
        if (isLoading || error || allStepsComplete) {
            return;
        }
        if (isCelebrating || hasProfileCompleteCelebration()) {
            stopCelebration();
        }
    }, [allStepsComplete, error, isCelebrating, isLoading]);

    useEffect(() => {
        if (!enabled || isLoading || error || hasProfileCompleteCelebrationDismissed(userScope)) {
            return;
        }

        const fromProfileComplete = fromParam === PROFILE_COMPLETE_FROM_PARAM;
        if (!fromProfileComplete && !hasProfileCompleteCelebration()) {
            return;
        }

        if (!allStepsComplete) {
            stopCelebration();
            return;
        }

        startCelebration();
        if (fromProfileComplete) {
            router.replace(RETURN_PATH[returnTo]);
        }
    }, [allStepsComplete, enabled, error, fromParam, isLoading, returnTo, router, userScope]);

    useEffect(() => {
        if (!allStepsComplete) {
            setIsAlreadyCompleteOnLoad(false);
        }
    }, [allStepsComplete]);

    useEffect(() => {
        if (!enabled || isLoading || error || hasProfileCompleteCelebrationDismissed(userScope)) {
            return;
        }

        if (!hasRecordedInitialCount.current) {
            hasRecordedInitialCount.current = true;
            previousCompletedCount.current = completedCount;

            const returnedFromFlow = allStepsComplete && hasProfileCompleteFlowOrigin();
            if (returnedFromFlow) {
                startCelebration();
                return;
            }

            const noCelebrationTrigger = !hasProfileCompleteCelebration() && fromParam !== PROFILE_COMPLETE_FROM_PARAM;

            if (allStepsComplete && noCelebrationTrigger) {
                setIsAlreadyCompleteOnLoad(true);
            }
            return;
        }

        const previous = previousCompletedCount.current;
        previousCompletedCount.current = completedCount;

        const completedLastStep = !isProfileComplete && allStepsComplete && previous !== null && previous < totalCount;
        const returnedFromFlow = allStepsComplete && hasProfileCompleteFlowOrigin();

        if (completedLastStep || returnedFromFlow) {
            setIsAlreadyCompleteOnLoad(false);
            startCelebration();
        }
    }, [
        allStepsComplete,
        completedCount,
        enabled,
        error,
        fromParam,
        isLoading,
        isProfileComplete,
        totalCount,
        userScope,
    ]);

    return {
        isSuccess: isCelebrating && allStepsComplete,
        isDismissed: isDismissed && allStepsComplete,
        isAlreadyCompleteOnLoad: shouldHideAsAlreadyComplete,
    };
}
